import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Save } from 'lucide-react';
import { useUpdateTransactionLimitsConfig } from '../../../hooks/useApi';
import { TransactionLimitsConfig, SignupBonusConfig } from '../../../types';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { LoadingSpinner } from '../../ui/Loading';

interface ConfigFormProps {
  config: TransactionLimitsConfig;
  onSuccess: () => void;
  canEdit?: boolean;
}

/** Form fields only — maxCashout / rev-share are derived or fixed on save. */
type SignupBonusFormValues = Pick<
  SignupBonusConfig,
  'enabled' | 'amountUsd' | 'winThresholdUsd' | 'expiryDays' | 'dailyGrantBudget'
>;

const DEFAULT_FORM: SignupBonusFormValues = {
  enabled: false,
  amountUsd: 5,
  winThresholdUsd: 30,
  expiryDays: 7,
  dailyGrantBudget: 100,
};

const SignupBonusConfigForm: React.FC<ConfigFormProps> = ({ config, onSuccess, canEdit = true }) => {
  const updateConfig = useUpdateTransactionLimitsConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const current = config.transactionLimits.signupBonus;

  const { register, handleSubmit, formState: { errors } } = useForm<SignupBonusFormValues>({
    defaultValues: {
      ...DEFAULT_FORM,
      enabled: current?.enabled ?? DEFAULT_FORM.enabled,
      amountUsd: current?.amountUsd ?? DEFAULT_FORM.amountUsd,
      winThresholdUsd: current?.winThresholdUsd ?? DEFAULT_FORM.winThresholdUsd,
      expiryDays: current?.expiryDays ?? DEFAULT_FORM.expiryDays,
      dailyGrantBudget: current?.dailyGrantBudget ?? DEFAULT_FORM.dailyGrantBudget,
    },
  });

  const onSubmit = async (data: SignupBonusFormValues) => {
    setIsSubmitting(true);
    try {
      // CRITICAL (money): backend REPLACES the whole transactionLimits JSONB block.
      // maxCashout always mirrors grant; rev-share unused → fixed 0.
      const signupBonus: SignupBonusConfig = {
        ...data,
        minDepositUsd: current?.minDepositUsd && current.minDepositUsd > 0
          ? current.minDepositUsd
          : 10,
        maxCashoutUsd: data.amountUsd,
        providerRevSharePct: 0,
      };
      await updateConfig.mutateAsync({
        transactionLimits: {
          ...config.transactionLimits,
          signupBonus,
        },
      });
      onSuccess();
    } catch (error) {
      // handled by the hook toast
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <fieldset disabled={!canEdit} className="space-y-6 min-w-0 border-0 p-0 m-0">
        <Card>
          <CardHeader>
            <CardTitle>Welcome Bonus</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              New players get an inactive welcome bonus record. When they claim it, shop/platform
              credit inventory funds their bonus. While active, withdrawals must keep at least the
              granted amount — unless event P&L (game ledger since claim) is ≤ −grant (floor lifts
              until event P&L recovers). Reaching peak event P&L ≥ threshold moves the bonus to
              In review (floor off; every payout needs manual approval — shop for shop players,
              admin for platform players). The first approved payout completes the bonus; after
              that, normal auto-approve rules apply. This is not the player&apos;s all-time game P&L.
              Idle players are checked on login (inactivity window) — grant must be fully recalled
              before status becomes expired. In review never expires until that first payout approval.
            </p>

            <label className="flex items-center gap-3 rounded-lg border p-3">
              <input
                type="checkbox"
                className="h-4 w-4"
                {...register('enabled')}
              />
              <span className="text-sm font-medium">
                Enable Welcome Bonus (off = no player is granted)
              </span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Grant Amount (USD)</label>
                <Input
                  type="number"
                  step="0.01"
                  {...register('amountUsd', {
                    valueAsNumber: true,
                    required: 'This field is required',
                    min: { value: 0.01, message: 'Must be greater than 0' },
                  })}
                  error={errors.amountUsd?.message}
                />
                <p className="mt-1 text-xs text-muted-foreground">Bonus credits funded when the player claims.</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Net Win Threshold (USD)</label>
                <Input
                  type="number"
                  step="0.01"
                  {...register('winThresholdUsd', {
                    valueAsNumber: true,
                    required: 'This field is required',
                    min: { value: 0, message: 'Must be 0 or greater' },
                  })}
                  error={errors.winThresholdUsd?.message}
                />
                <p className="mt-1 text-xs text-muted-foreground">Peak event P&L (since claim) to enter In review (e.g. $30).</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Inactivity window (days)</label>
                <Input
                  type="number"
                  {...register('expiryDays', {
                    valueAsNumber: true,
                    required: 'This field is required',
                    min: { value: 1, message: 'Must be at least 1 day' },
                  })}
                  error={errors.expiryDays?.message}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Not a calendar expiry date. While active, the next login expires the bonus only if
                  the player has been claimed ≥ N days and had no game transfers in the last N days —
                  and only after the grant is fully recalled from the master wallet.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Daily claim limit (players)</label>
                <Input
                  type="number"
                  {...register('dailyGrantBudget', {
                    valueAsNumber: true,
                    required: 'This field is required',
                    min: { value: 1, message: 'Must be at least 1' },
                  })}
                  error={errors.dailyGrantBudget?.message}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Max number of players who can claim the welcome bonus per day (server local day).
                  When the limit is reached, new claims are blocked until tomorrow — players see
                  &quot;Please wait until tomorrow&quot;.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </fieldset>

      {canEdit && (
        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting || updateConfig.isLoading}>
            {isSubmitting || updateConfig.isLoading ? (
              <>
                <LoadingSpinner className="h-4 w-4 mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Configuration
              </>
            )}
          </Button>
        </div>
      )}
    </form>
  );
};

export default SignupBonusConfigForm;
